import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { getSupplierContactListDataBySupplierId } from '../supplierContacts/mockData'
import { getSupplierLastSelectedStateDataBySupplierId } from '../supplierLastSelectedStates/mockData'
import {
  updateSupplierInSupplierProducts,
  getSupplierProductWithSupplierProductItemsData,
} from '../supplierProducts/mockData'
import {
  updateSupplierInTsdSuppliers,
  getTsdSupplierListDataBySupplierId,
} from '../tsdSuppliers/mockData'
import { requireAuthenticatedUser } from '../../decorators'
import {
  getSupplierListData,
  getSupplierOverviewData,
  getSupplierProductsListData,
  getProductGroupsForSupplierListData,
  getSupplierProductDataForSupplierAndProduct,
  getSupplierOpportunityRegistrationEmailAddressListDataBySupplier,
} from './mockData'
import type {
  ISupplier,
  ISupplierOverview,
  ISupplierLastSelectedState,
} from '~/types/supplier/models'

interface GetSuppliersResponse {
  data: ISupplier[] | undefined
}

async function getSuppliers(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetSuppliersResponse = {
    data: await getSupplierListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

interface GetSuppliersOverviewResponse {
  data: ISupplierOverview[] | undefined
}

async function getSupplierOverview_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const responseBody = { data: await getSupplierOverviewData(id) }
  console.log(`'Returning overview for supplier "${id}".`)
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getSupplierContactsBySupplierId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning contacts for supplier "${id}".`)
  const data = await getSupplierContactListDataBySupplierId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

interface GetSupplierLastSelectedStateResponse {
  data: ISupplierLastSelectedState
}

async function getSupplierLastSelectedStateById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetSupplierLastSelectedStateResponse = {
      data: await getSupplierLastSelectedStateDataBySupplierId(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Supplier ${id} last selected data: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

async function getSupplierProductsList_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const responseBody = { data: await getSupplierProductsListData(id) }
  console.log(`'Returning supplierProducts for supplier "${id}".`)
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getTsdSuppliers_bySupplierId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`Returning tsdSuppliers for supplier "${id}".`)
  return res(
    ctx.status(200),
    ctx.json({ data: await getTsdSupplierListDataBySupplierId(id) })
  )
}

async function getSupplierOpportunityRegistrationEmailAddresses_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning supplierProducts for supplier "${id}".`)
  return res(
    ctx.status(200),
    ctx.json({
      data: await getSupplierOpportunityRegistrationEmailAddressListDataBySupplier(
        id
      ),
    })
  )
}

async function getProductGroupsForSupplierList_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const responseBody = {
    data: await getProductGroupsForSupplierListData(id),
  }
  console.log(`'Returning productGroups select list for supplier "${id}".`)
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getSupplierProductForSupplierAndProduct_byIds(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { supplierId, productId } = req.params
  if (typeof supplierId !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed supplierId is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  if (typeof productId !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed productId is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(
    `'Returning supplierProduct for supplier "${supplierId}" and product "${productId}.`
  )
  const responseBody = {
    data: await getSupplierProductDataForSupplierAndProduct(
      supplierId,
      productId
    ),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getSupplierProductWithItemsForSupplierAndProduct_byIds(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { supplierId, productId } = req.params
  if (typeof supplierId !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed supplierId is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  if (typeof productId !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed productId is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }

  const wellFormedSupplierProductsOnly =
    req.url.searchParams.get('wellformedsupplierproductsonly') === 'true'

  console.log(
    `Returning supplierProductsWithItems for supplier '${supplierId}', product '${productId}'; ` +
      ` wellFormedSupplierProductsOnly = ${wellFormedSupplierProductsOnly}.`
  )

  const supplierProductData = await getSupplierProductDataForSupplierAndProduct(
    supplierId,
    productId,
    wellFormedSupplierProductsOnly
  )
  if (!supplierProductData) {
    const responseBody = {
      statusCode: 404,
      message: `No supplierProduct for supplierId "${supplierId}" and productId "${productId}"`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
  console.log(
    `'Found supplierProduct for supplier "${supplierId}" and product "${productId}:`,
    supplierProductData
  )
  const supplierProductDataWithItems =
    await getSupplierProductWithSupplierProductItemsData({
      id: supplierProductData.id,
      doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem: true,
    })

  console.log(
    'Returning supplierProduct with items data (supplierProduct in items replaced by entitySelectItem):',
    supplierProductDataWithItems
  )
  return res(ctx.status(200), ctx.json({ data: supplierProductDataWithItems }))
}

// post

const postSupplier = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getSupplierListData)
}

const patchSupplier = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getSupplierListData, [
    updateSupplierInSupplierProducts,
    updateSupplierInTsdSuppliers,
  ])
}

const deleteSupplier = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getSupplierListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/suppliers',
    handler: getSuppliers,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/overview',
    handler: getSupplierOverview_byId,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/lastselectedstate',
    handler: getSupplierLastSelectedStateById,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/supplierProducts',
    handler: getSupplierProductsList_byId,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/tsdSuppliers',
    handler: getTsdSuppliers_bySupplierId,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/contacts',
    handler: getSupplierContactsBySupplierId,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/opportunityregistrationemailaddresses',
    handler: getSupplierOpportunityRegistrationEmailAddresses_byId,
  },
  {
    method: 'get',
    path: '/api/suppliers/:id/productGroups',
    handler: getProductGroupsForSupplierList_byId,
  },
  {
    method: 'get',
    path: '/api/suppliers/:supplierId/product/:productId',
    handler: getSupplierProductForSupplierAndProduct_byIds,
  },
  {
    method: 'get',
    path: '/api/suppliers/:supplierId/product/:productId/withitems',
    handler: getSupplierProductWithItemsForSupplierAndProduct_byIds,
  },
  {
    method: 'post',
    path: '/api/suppliers',
    handler: postSupplier,
  },
  {
    method: 'patch',
    path: '/api/suppliers/:id',
    handler: patchSupplier,
  },
  {
    method: 'delete',
    path: '/api/suppliers/:id',
    handler: deleteSupplier,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
